/** Modern Hangul composition. Compatibility jamo are teaching keys, not NFC jamo. */
export const INITIALS = [...'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ'];
export const VOWELS = [...'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ'];
export const FINALS = ['', ...'ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ'];
export const VOWEL_CORNERS = {'ㅘ':['ㅗ','ㅏ'],'ㅙ':['ㅗ','ㅐ'],'ㅚ':['ㅗ','ㅣ'],'ㅝ':['ㅜ','ㅓ'],'ㅞ':['ㅜ','ㅔ'],'ㅟ':['ㅜ','ㅣ'],'ㅢ':['ㅡ','ㅣ']};
export function splitBlock(text) {
  const value = String(text).normalize('NFC');
  const code = value.codePointAt(0) - 0xac00;
  if (value.length !== 1 || code < 0 || code >= 11172) return null;
  return [INITIALS[Math.floor(code / 588)], VOWELS[Math.floor(code / 28) % 21], FINALS[code % 28]].filter(Boolean);
}
export function joinBlock(parts) {
  const [initial, vowel, final = ''] = parts;
  const i = INITIALS.indexOf(initial), v = VOWELS.indexOf(vowel), f = FINALS.indexOf(final);
  if (parts.length < 2 || parts.length > 3 || i < 0 || v < 0 || f < 0) return parts.join('');
  return String.fromCodePoint(0xac00 + (i * 21 + v) * 28 + f);
}
export function blockLayout(vowel) {
  return 'ㅗㅛㅜㅠㅡ'.includes(vowel) ? 'horizontal' : 'ㅘㅙㅚㅝㅞㅟㅢ'.includes(vowel) ? 'combined' : 'vertical';
}
export function blockParts(text) {
  return splitBlock(text) || [...String(text).replaceAll('_', '')];
}
/** Deliberately one block: a new word is built with whole syllable keys elsewhere. */
export function addBlockKey(value, key, maximum = 3) {
  const parts = blockParts(value);
  const allowed = parts.length === 0 ? INITIALS : parts.length === 1 ? VOWELS : FINALS.slice(1);
  return parts.length >= maximum || !allowed.includes(key) ? value : joinBlock([...parts, key]);
}
export function deleteBlockKey(value) {
  return joinBlock(blockParts(value).slice(0, -1));
}
export function blockHint(answer, level) {
  const parts = splitBlock(answer);
  return { slots: parts.map((part, i) => i < level ? part : '_'), solution: level >= parts.length };
}
/** Keep the correctly built prefix so the next tap can repair its next component. */
export function repairBlock(actual, answer) {
  const parts = blockParts(actual), expected = splitBlock(answer);
  let kept = 0;
  while (kept < expected.length && parts[kept] === expected[kept]) kept++;
  return { value: joinBlock(expected.slice(0, kept)), kept, gaps: expected.length - kept };
}

export const wordElements = text => [...String(text).normalize('NFC')].flatMap(g => (splitBlock(g) || [g]).flatMap(part => SPLIT_FINAL[part] || [part]));
const FINAL_PAIRS = {'ㄱㅅ':'ㄳ','ㄴㅈ':'ㄵ','ㄴㅎ':'ㄶ','ㄹㄱ':'ㄺ','ㄹㅁ':'ㄻ','ㄹㅂ':'ㄼ','ㄹㅅ':'ㄽ','ㄹㅌ':'ㄾ','ㄹㅍ':'ㄿ','ㄹㅎ':'ㅀ','ㅂㅅ':'ㅄ'};
const SPLIT_FINAL = Object.fromEntries(Object.entries(FINAL_PAIRS).map(([parts,final])=>[final,[...parts]]));
const VOWEL_PAIRS = {'ㅗㅏ':'ㅘ','ㅗㅐ':'ㅙ','ㅗㅣ':'ㅚ','ㅜㅓ':'ㅝ','ㅜㅔ':'ㅞ','ㅜㅣ':'ㅟ','ㅡㅣ':'ㅢ','ㅘㅣ':'ㅙ','ㅝㅣ':'ㅞ'};
/** A small two-set Korean composer: vowel entry moves a pending final into the next block. */
export function addWordKey(value,key){
  const text=String(value).normalize('NFC'),chars=[...text],last=chars.pop(),prefix=chars.join('');
  if(/^[.,!?…。0-9]$/u.test(key))return text+key;
  if(!last)return INITIALS.includes(key)||key===' '?key:text;
  if(key===' ')return text+' ';
  const parts=splitBlock(last);
  if(!parts){
    if(INITIALS.includes(last)&&VOWELS.includes(key))return prefix+joinBlock([last,key]);
    return INITIALS.includes(key)?text+key:text;
  }
  const [initial,vowel,final]=parts;
  if(VOWELS.includes(key)){
    if(final){
      const pair=SPLIT_FINAL[final];
      return prefix+joinBlock([initial,vowel,...(pair?[pair[0]]:[])])+joinBlock([pair?.[1]||final,key]);
    }
    const combined=VOWEL_PAIRS[vowel+key];
    return combined?prefix+joinBlock([initial,combined]):text;
  }
  if(!INITIALS.includes(key))return text;
  if(!final&&FINALS.includes(key))return prefix+joinBlock([initial,vowel,key]);
  const combined=final&&FINAL_PAIRS[final+key];
  return combined?prefix+joinBlock([initial,vowel,combined]):text+key;
}
export function deleteWordKey(value){
  const chars=[...String(value).normalize('NFC')],last=chars.pop(),parts=last&&splitBlock(last);
  if(parts)chars.push(joinBlock(parts.slice(0,-1)));
  return chars.join('');
}
/** Preserve complete correct syllables; the learner retypes the remaining letters normally. */
export function repairWordPrefix(actual,expected){
  const a=[...String(actual).normalize('NFC')],e=[...String(expected).normalize('NFC')];let kept=0;
  while(kept<e.length&&a[kept]===e[kept])kept++;
  return {value:e.slice(0,kept).join(''),kept,gaps:e.length-kept};
}
